import {useCallback, useMemo, useState} from 'react';

import styled from 'styled-components';

import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import ClickOutsideDiv, {ClickOutsideDivProps} from '~/shared/components/ClickOutsideDiv';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {SupportedLanguages, DEFAULT_CULTURE, DEFAULT_LANGUAGE_KEY, EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {setCurrentLanguageCookie} from '~/shared/utils/cookies';
import Button from '~/shared/components/Button';
import {changeLanguagePrefixToUrlOrPath, getLocalizationService} from '~/shared/services/localisationService';
import {Link} from '~/shared/router';
import pieUsIcon from '~/assets/images/icons/pie_us.svg';
import pieIsraelIcon from '~/assets/images/icons/pie_israel.svg';
import ImageWithAlt from '~/shared/components/ImageWithAlt';

const PIE_MAP_ICONS = {
  he: pieIsraelIcon,
  en: pieUsIcon,
};

// #region styled components
const Root = styled.div`
  display: flex;
`;

const ChangeLangButton = styled(Button)`
  direction: ltr;
  ${flexCenterVertically}
  height: 30px;
  align-self: center;
  padding: 0;
  ${flipOnLTR`
    margin-right: 20px;
    ${media.minDesktop`
      margin-right: 32px;
    `}
  `}
`;

const CircleFlag = styled(ImageWithAlt)<{isOpen?: boolean}>`
  cursor: pointer;
  border-radius: 50%;
  border-width: 6px;
  border-style: solid;
  width: 32px;
  height: 32px;
  ${({theme, isOpen}) => `
    background-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
    border-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
    
    :hover {
      border-color: ${theme.colors.secondaryHover};
      background-color: ${theme.colors.secondaryHover};
  `}
`;

const ClickOutsideWithoutExtraProps = ({isOpen: _ignore, ...rest}: ClickOutsideDivProps & {
  isOpen: boolean;
}) => <ClickOutsideDiv {...rest} />;

const Dropdown = styled(ClickOutsideWithoutExtraProps).attrs({
  tabIndex: -1,
})`
  direction: ltr !important;
  display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
  width: 160px;
  position: absolute;
  top: 35px;
  ${flipOnLTR`
    left: 0px;
  `}
  padding: 10px 0;
  box-shadow: ${({theme}) => theme.shadows.shadow2};
  background: ${({theme}) => theme.colors.surface};
  border-radius: 12px;
  color: ${({theme}) => theme.colors.gray000};
  z-index: ${({theme}) => theme.zIndex.languageChangeButton};
`;

const languageOptionAttrs = () => ({role: 'link'});
const LanguageOption = styled(Link).attrs(languageOptionAttrs)`
  ${flexCenterVertically}
  height: 40px;
  cursor: pointer;
  padding: 0 20px;

  &:hover {
    background: ${({theme}) => theme.colors.gray910};
  }
`;

const OptionText = styled.div`
  margin-left: 10px;
`;
// #endregion

export const changeLanguage = (languageKey = DEFAULT_LANGUAGE_KEY) => {
  const culture =
    SupportedLanguages.find(({key: supportedLanguageKey}) => supportedLanguageKey === languageKey)?.culture ||
    DEFAULT_CULTURE;

  setCurrentLanguageCookie({culture, languageKey});
  // eslint-disable-next-line no-restricted-properties
  const {pathname, search} = window.location || '';
  // eslint-disable-next-line no-restricted-properties
  let fullPathname = pathname;
  if (search) {
    fullPathname += search;
  }
  // eslint-disable-next-line no-restricted-properties
  const newUrl = changeLanguagePrefixToUrlOrPath(fullPathname, languageKey);
  // eslint-disable-next-line no-restricted-properties
  window.location.replace(newUrl);
};

const LanguageSelector = ({className = ''}: {
  className?: string;
}) => {
  const {t, currentLanguageKey} = getLocalizationService();
  const [isOpen, setIsOpen] = useState(false);
  const currentLanguageObj = useMemo(
    () => SupportedLanguages.find(({key}) => key === currentLanguageKey) || EMPTY_OBJECT,
    [currentLanguageKey],
  );

  const onDropdownClickOutside = useCallback((e: Event) => {
    if (!isOpen) {
      return;
    }

    e.stopPropagation();
    setIsOpen(false);
  }, [isOpen]);

  const handleLanguageSelection = useCallback(
    (languageKey = DEFAULT_LANGUAGE_KEY) => {
      if (currentLanguageObj.key === languageKey) {
        setIsOpen(false);
        return;
      }
      setIsOpen(false);

      changeLanguage(languageKey);
    },
    [currentLanguageObj.key],
  );

  return (
    <Root className={className}>
      <ChangeLangButton
        isOpen={isOpen}
        onClick={() => setIsOpen(s => !s)}
        isExpandable
        aria-controls="header-language-dropdown"
        aria-label={t('change_language')}
      >
        <CircleFlag src={PIE_MAP_ICONS[currentLanguageObj.icon]} alt={currentLanguageObj.name} isOpen={isOpen} />
      </ChangeLangButton>

      <Dropdown isOpen={isOpen} onClickOutside={onDropdownClickOutside} id="header-language-dropdown">
        {SupportedLanguages.map(({key, name, icon}) => (
          <LanguageOption key={key} onClick={() => handleLanguageSelection(key)}>
            <CircleFlag src={PIE_MAP_ICONS[icon]} noAlt />
            <OptionText>{name}</OptionText>
          </LanguageOption>
        ))}
      </Dropdown>
    </Root>
  );
};

export default LanguageSelector;
